<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="vendors-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('vendors.title') }}</h1>
          <p class="page-subtitle">Manage your supplier directory</p>
        </div>
        <IntercomButton variant="primary" @click="showCreateModal = true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('vendors.addVendor') }}</span>
          <kbd class="shortcut-hint">N</kbd>
        </IntercomButton>
      </div>

      <!-- Search Bar -->
      <div class="search-bar">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('common.search') + '...'"
            @input="handleSearchInput"
          />
          <kbd v-if="!searchQuery" class="search-shortcut-hint">/</kbd>
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Vendors Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="vendors"
          :loading="isLoading"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          :selected-index="selectedIndex"
          @sort="handleSort"
          hoverable
          clickable
          @row-click="handleEdit"
        >
          <template #cell-gstin="{ value }">
            {{ value || '-' }}
          </template>

          <template #cell-state_code="{ value }">
            {{ value || '-' }}
          </template>

          <template #cell-outstanding_balance="{ value }">
            <span :style="value > 0 ? { color: '#dc2626', fontWeight: 600 } : {}">
              {{ formatCurrency(value) }}
            </span>
          </template>

          <template #cell-actions="{ row }">
            <div style="display: flex; gap: var(--space-2)">
              <IntercomButton size="sm" variant="ghost" @click.stop="router.push(`/vendors/${row.id}/ledger`)">
                Ledger
              </IntercomButton>
              <IntercomButton size="sm" variant="ghost" @click.stop="handleEdit(row)">
                {{ $t('common.edit') }}
              </IntercomButton>
              <IntercomButton size="sm" variant="ghost" @click.stop="handleDelete(row)">
                {{ $t('common.delete') }}
              </IntercomButton>
            </div>
          </template>
        </IntercomTable>
      </IntercomCard>
      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset = v; fetch() }"
      />

      <!-- Create/Edit Modal -->
      <VendorForm
        v-model:show="showCreateModal"
        :vendor="selectedVendor"
        @success="handleFormSuccess"
      />
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { usePageShortcuts } from '@shared/composables/usePageShortcuts.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomPagination,
} from '@shared/components/intercom'
import VendorForm from '../components/VendorForm.vue'
import { billingNavSections } from '../config/navigation.js'

const { t } = useI18n()
const router = useRouter()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const { listVendors, deleteVendor, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

// State
const {
  items: vendors,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList((params) => listVendors(params))

const showCreateModal = ref(false)
const selectedVendor = ref(null)
const sortBy = ref('updated_at')
const sortOrder = ref('desc')
const searchInputRef = ref(null)

// Keyboard shortcuts for list view
const { selectedIndex } = usePageShortcuts({
  items: vendors,
  onNew: () => { selectedVendor.value = null; showCreateModal.value = true },
  onOpen: (item) => handleEdit(item),
  onEdit: (item) => handleEdit(item),
  onDelete: (item) => handleDelete(item),
  searchRef: searchInputRef,
})

// Table columns
const columns = [
  { key: 'vendor_number', title: t('vendors.vendorNumber'), width: '130px' },
  { key: 'name', title: t('vendors.name'), width: '220px', sortable: true },
  { key: 'phone', title: t('vendors.phone'), width: '150px', sortable: true },
  { key: 'gstin', title: t('vendors.gstin'), width: '170px' },
  { key: 'state_code', title: t('vendors.stateCode'), width: '100px' },
  { key: 'outstanding_balance', title: 'Outstanding', width: '140px', align: 'right', format: (v) => formatCurrency(v) },
  { key: 'actions', title: 'Actions', width: '210px', align: 'right' },
]

const formatCurrency = (val) => {
  if (val == null) return '--'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val)
}

/**
 * Handle edit vendor
 */
const handleEdit = (vendor) => {
  selectedVendor.value = vendor
  showCreateModal.value = true
}

/**
 * Handle delete vendor
 */
const handleDelete = async (vendor) => {
  const ok = await confirm(`Delete vendor "${vendor.name}"?`, { title: 'Delete Vendor', type: 'error' })
  if (!ok) {
    return
  }

  try {
    await deleteVendor(vendor.id)
    await fetch()
  } catch (error) {
    console.error('Failed to delete vendor:', error)
  }
}

/**
 * Handle form success (create/update)
 */
const handleFormSuccess = async () => {
  showCreateModal.value = false
  selectedVendor.value = null
  await fetch()
}

const handleSort = ({ sortBy: newSort, sortOrder: newOrder }) => {
  sortBy.value = newSort
  sortOrder.value = newOrder
  resetAndFetch({ sort_by: newSort, sort_order: newOrder })
}

const searchQuery = ref('')
const handleSearchInput = () => { resetAndFetch({ search: searchQuery.value, sort_by: sortBy.value, sort_order: sortOrder.value }) }
const clearSearch = () => { searchQuery.value = ''; resetAndFetch({ search: '', sort_by: sortBy.value, sort_order: sortOrder.value }) }

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.vendors-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* Search Bar */
.search-bar { display: flex; gap: var(--space-3); margin-bottom: var(--space-6); }
.search-input-wrapper { flex: 1; position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 14px; color: var(--color-gray-400); pointer-events: none; }
.search-input { width: 100%; height: 44px; padding: 0 var(--space-10) 0 44px; font-size: var(--font-size-base); color: var(--color-charcoal); background: var(--color-white); border: 1px solid var(--color-gray-200); border-radius: var(--radius-lg); outline: none; transition: all var(--transition-fast); }
.search-input:focus { border-color: var(--color-blue); box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1); }
.search-clear { position: absolute; right: 12px; display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: var(--color-gray-100); border: none; border-radius: 50%; cursor: pointer; color: var(--color-gray-500); transition: all var(--transition-fast); }
.search-clear:hover { background: var(--color-gray-200); color: var(--color-gray-700); }

@media (max-width: 768px) {
  .vendors-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .page-title {
    font-size: 26px;
  }
}
</style>
